import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { movieApi, subscriptionApi, adminApi } from '../api/client';

// ── Movies ────────────────────────────────────────────────────────────────────

export function useMovies(enabled = true) {
  return useQuery({
    queryKey: ['movies'],
    queryFn: async () => {
      const { data } = await movieApi.getAll();
      if (!data.success) throw new Error('Failed to load movies');
      return data.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMovieDetail(movieId) {
  return useQuery({
    queryKey: ['movie', movieId],
    queryFn: async () => {
      const { data } = await movieApi.getDetail(movieId);
      if (!data.success) throw new Error(data.message || 'Not found');
      return data.data;
    },
    enabled: !!movieId,
    staleTime: 2 * 60 * 1000,
  });
}

export function useEpisodeDetail(episodeId) {
  return useQuery({
    queryKey: ['episode', episodeId],
    queryFn: async () => {
      const { data } = await movieApi.getEpisodeDetail(episodeId);
      if (!data.success) throw new Error(data.message || 'Not found');
      return data.data;
    },
    enabled: !!episodeId,
  });
}

// General catalogue search — hits the same /api/MovieApi/Search endpoint the mobile app uses.
// Pass an empty/undefined query with no categoryId to get the full live catalogue.
export function useMovieSearch(query, categoryId, options = {}) {
  const { enabled = true } = options;
  return useQuery({
    queryKey: ['movies', 'search', query || '', categoryId || null],
    queryFn: async () => {
      const { data } = await movieApi.search(query, categoryId);
      if (!data.success) throw new Error('Search failed');
      return data.data;
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useMoviesByCategory(categoryId, search) {
  return useQuery({
    queryKey: ['movies', 'category', categoryId, search || ''],
    queryFn: async () => {
      const { data } = await movieApi.getByCategory(categoryId, search);
      if (!data.success) throw new Error('Failed');
      return data.data;
    },
    enabled: !!categoryId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useComments(episodeId) {
  return useQuery({
    queryKey: ['comments', episodeId],
    queryFn: async () => {
      const { data } = await movieApi.getComments(episodeId, 1, 30);
      if (!data.success) throw new Error('Failed to load comments');
      return data.data || [];
    },
    enabled: !!episodeId,
  });
}

// Legacy name kept for backward-compat with existing components
export function useCheckSubscription() {
  return useQuery({
    queryKey: ['subscriptionStatus'],
    queryFn: async () => {
      const { data } = await movieApi.checkSubscription();
      if (!data.success) throw new Error('Failed to check subscription');
      return {
        hasSubscription: data.hasVideoSubscription,
        subscribeExpiryDate: data.subscribeExpiryDate,
        subscriptionPriceNaira: data.subscriptionPriceNaira ?? 2000,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useReactToEpisode() {
  const qc = useQueryClient();
  return useMutation({
    // reaction uses the richer Like/Dislike/DoubleLike/None API. Keep isLike accepted for
    // older callers while the player moves over to the new endpoint.
    mutationFn: ({ episodeId, reaction, isLike }) => reaction != null
      ? movieApi.setEpisodeReaction(episodeId, reaction)
      : movieApi.reactToEpisode(episodeId, isLike),
    onSuccess: (_, { episodeId }) => {
      qc.invalidateQueries(['episode', episodeId]);
    },
  });
}

export function useReactToMovie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, reaction }) => movieApi.setMovieReaction(movieId, reaction),
    onSuccess: (response, { movieId }) => {
      const reactions = response?.data?.data;
      if (reactions) {
        qc.setQueriesData({ queryKey: ['movie'] }, (old) => {
          if (!old) return old;
          const oldId = old.Id ?? old.id ?? old.MovieId;
          return String(oldId) === String(movieId) ? { ...old, Reactions: reactions } : old;
        });
      }
      qc.invalidateQueries({ queryKey: ['movies'] });
    },
  });
}

export function usePostComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ episodeId, commentText }) => movieApi.postComment(episodeId, commentText),
    onSuccess: (_, { episodeId }) => {
      qc.invalidateQueries(['comments', episodeId]);
    },
  });
}

// ── Upload ────────────────────────────────────────────────────────────────────

export function useUploadMovie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ formData, onUploadProgress }) => movieApi.uploadMovie(formData, onUploadProgress),
    onSuccess: () => qc.invalidateQueries(['movies']),
  });
}

export function useUploadEpisode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ formData, onUploadProgress }) => movieApi.uploadEpisode(formData, onUploadProgress),
    onSuccess: () => qc.invalidateQueries(['movies']),
  });
}

// ── Content Creator: my uploads ───────────────────────────────────────

export function useMyMovies(enabled = true) {
  return useQuery({
    queryKey: ['myMovies'],
    queryFn: async () => {
      const { data } = await movieApi.getMyMovies();
      if (!data.success) throw new Error(data.message || 'Failed to load your uploads');
      return data.data || [];
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

// Propose changes to one of the creator's own titles. The live title is untouched until an admin
// approves, so the only thing that changes locally is the "pending edit" shown on the dashboard.
export function useSubmitMovieEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, payload, onUploadProgress }) =>
      movieApi.submitMovieEdit(movieId, payload, onUploadProgress),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['myMovies'] }),
  });
}

export function useCancelMovieEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (editId) => movieApi.cancelMovieEdit(editId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['myMovies'] }),
  });
}

export function useSubmitEpisodeEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ episodeId, payload, onUploadProgress }) =>
      movieApi.submitEpisodeEdit(episodeId, payload, onUploadProgress),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['myMovies'] }),
  });
}

export function useCancelEpisodeEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (editId) => movieApi.cancelEpisodeEdit(editId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['myMovies'] }),
  });
}

// ── Subscription ──────────────────────────────────────────────────────────────

export function useSubscriptionStatus(enabled = true) {
  return useQuery({
    queryKey: ['subscription', 'status'],
    queryFn: async () => {
      const { data } = await subscriptionApi.getStatus();
      if (!data.success) throw new Error('Failed');
      return data.data;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useInitiateSubscription() {
  return useMutation({
    mutationFn: ({ email, planId }) => subscriptionApi.initiate(email, planId),
  });
}

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: ['subscription', 'plans'],
    queryFn: async () => {
      const { data } = await subscriptionApi.getPlans();
      if (!data.success) throw new Error('Failed to load plans');
      return data.data;
    },
    staleTime: 10 * 60 * 1000,
  });
}

export function useCancelSubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => subscriptionApi.cancel(true), // callers must show the server's warning and get explicit confirmation first (see SubscribePage.handleCancel)
    onSuccess: () => {
      qc.invalidateQueries(['subscription']);
      qc.invalidateQueries(['subscriptionStatus']);
    },
  });
}

export function useVerifySubscription() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ reference }) => subscriptionApi.verify(reference),
    onSuccess: () => {
      qc.invalidateQueries(['subscription']);
      qc.invalidateQueries(['subscriptionStatus']);
    },
  });
}

// ── Admin ─────────────────────────────────────────────────────────────────────

export function usePendingMovies() {
  return useQuery({
    queryKey: ['admin', 'pendingMovies'],
    queryFn: async () => {
      const { data } = await adminApi.getPendingMovies();
      if (!data.success) throw new Error(data.message || 'Failed to load pending movies');
      return data.data;
    },
  });
}

export function usePendingEpisodes() {
  return useQuery({
    queryKey: ['admin', 'pendingEpisodes'],
    queryFn: async () => {
      const { data } = await adminApi.getPendingEpisodes();
      if (!data.success) throw new Error(data.message || 'Failed to load pending episodes');
      return data.data;
    },
  });
}

export function useApproveMovie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, status }) => adminApi.approveMovie(movieId, status),
    onSuccess: () => {
      qc.invalidateQueries(['admin', 'pendingMovies']);
      qc.invalidateQueries(['admin', 'processingContent']);
    },
  });
}

export function useApproveEpisode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ episodeId, status }) => adminApi.approveEpisode(episodeId, status),
    onSuccess: () => {
      qc.invalidateQueries(['admin', 'pendingEpisodes']);
      qc.invalidateQueries(['admin', 'processingContent']);
    },
  });
}

// Content approved but stuck waiting on (or failing) its Bunny.net push — not live yet.
export function useProcessingContent() {
  return useQuery({
    queryKey: ['admin', 'processingContent'],
    queryFn: async () => {
      const { data } = await adminApi.getProcessingContent();
      if (!data.success) throw new Error(data.message || 'Failed to load processing content');
      return data.data;
    },
    // Failed/queued pushes can resolve in the background, so keep this reasonably fresh.
    refetchInterval: 15000,
  });
}

// Creator edits to live titles, awaiting an admin's ruling (current vs. proposed values).
export function usePendingMovieEdits() {
  return useQuery({
    queryKey: ['admin', 'pendingMovieEdits'],
    queryFn: async () => {
      const { data } = await adminApi.getPendingMovieEdits();
      if (!data.success) throw new Error(data.message || 'Failed to load pending edits');
      return data.data || [];
    },
  });
}

export function useReviewMovieEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ editId, status, note }) => adminApi.reviewMovieEdit(editId, status, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pendingMovieEdits'] });
      // An approved edit changes the live title, so anything showing it is now out of date.
      qc.invalidateQueries({ queryKey: ['admin', 'catalog'] });
      qc.invalidateQueries({ queryKey: ['movies'] });
      qc.invalidateQueries({ queryKey: ['movie'] });
    },
  });
}

export function usePendingEpisodeEdits() {
  return useQuery({
    queryKey: ['admin', 'pendingEpisodeEdits'],
    queryFn: async () => {
      const { data } = await adminApi.getPendingEpisodeEdits();
      if (!data.success) throw new Error(data.message || 'Failed to load pending episode edits');
      return data.data || [];
    },
  });
}

export function useReviewEpisodeEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ editId, status, note }) => adminApi.reviewEpisodeEdit(editId, status, note),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pendingEpisodeEdits'] });
      // An approved edit changes the live episode.
      qc.invalidateQueries({ queryKey: ['admin', 'movie'] });
      qc.invalidateQueries({ queryKey: ['movie'] });
      qc.invalidateQueries({ queryKey: ['episode'] });
    },
  });
}

export function useRetryPushEpisode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminApi.retryPushEpisode(id),
    onSuccess: () => qc.invalidateQueries(['admin', 'processingContent']),
  });
}

export function useRetryPushTrailer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminApi.retryPushTrailer(id),
    onSuccess: () => qc.invalidateQueries(['admin', 'processingContent']),
  });
}

export function useSystemConfigs() {
  return useQuery({
    queryKey: ['admin', 'systemConfig'],
    queryFn: async () => {
      const { data } = await adminApi.getAllConfigs();
      if (!data.success) throw new Error(data.message || 'Failed to load system configuration');
      return data.data;
    },
  });
}

export function useUpdateSystemConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, value, description }) => adminApi.updateConfig(key, value, description),
    onSuccess: () => qc.invalidateQueries(['admin', 'systemConfig']),
  });
}

// ── Cast / Actors ────────────────────────────────────────────

export function useMovieCast(movieId) {
  return useQuery({
    queryKey: ['movieCast', movieId],
    queryFn: async () => {
      const { data } = await movieApi.getMovieCast(movieId);
      if (!data.success) throw new Error('Failed to load cast');
      return data.data;
    },
    enabled: !!movieId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useMoviesByActor(actorId) {
  return useQuery({
    queryKey: ['actor', actorId, 'movies'],
    queryFn: async () => {
      const { data } = await movieApi.getMoviesByActor(actorId);
      if (!data.success) throw new Error(data.message || 'Actor not found');
      return data.data;
    },
    enabled: !!actorId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useActors() {
  return useQuery({
    queryKey: ['actors'],
    queryFn: async () => {
      const { data } = await movieApi.getActors();
      if (!data.success) throw new Error('Failed to load actors');
      return data.data;
    },
    staleTime: 10 * 60 * 1000,
  });
}

// ── My List ────────────────────────────────────────────────────

export function useMyList(enabled = true) {
  return useQuery({
    queryKey: ['myList'],
    queryFn: async () => {
      const { data } = await movieApi.getMyList();
      if (!data.success) throw new Error('Failed to load My List');
      return data.data;
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useAddToMyList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (movieId) => movieApi.addToMyList(movieId),
    onSuccess: (_, movieId) => {
      qc.invalidateQueries(['myList']);
      qc.invalidateQueries(['movie', String(movieId)]);
    },
  });
}

export function useRemoveFromMyList() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (movieId) => movieApi.removeFromMyList(movieId),
    onSuccess: (_, movieId) => {
      qc.invalidateQueries(['myList']);
      qc.invalidateQueries(['movie', String(movieId)]);
    },
  });
}

// ── Admin: Subscription Plans ─────────────────────────────────

export function useAdminSubscriptionPlans() {
  return useQuery({
    queryKey: ['admin', 'subscriptionPlans'],
    queryFn: async () => {
      const { data } = await adminApi.getSubscriptionPlans();
      if (!data.success) throw new Error('Failed to load plans');
      return data.data;
    },
  });
}

export function useCreateSubscriptionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) => adminApi.createSubscriptionPlan(payload),
    onSuccess: () => qc.invalidateQueries(['admin', 'subscriptionPlans']),
  });
}

export function useUpdateSubscriptionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => adminApi.updateSubscriptionPlan(id, payload),
    onSuccess: () => qc.invalidateQueries(['admin', 'subscriptionPlans']),
  });
}

export function useDeleteSubscriptionPlan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminApi.deleteSubscriptionPlan(id),
    onSuccess: () => qc.invalidateQueries(['admin', 'subscriptionPlans']),
  });
}

// ── Admin: Actors ────────────────────────────────────────────

export function useAdminActors() {
  return useQuery({
    queryKey: ['admin', 'actors'],
    queryFn: async () => {
      const { data } = await adminApi.getActors();
      if (!data.success) throw new Error('Failed to load actors');
      return data.data;
    },
  });
}

export function useCreateActor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ name, bio, imageFile }) => adminApi.createActor(name, bio, imageFile),
    onSuccess: () => { qc.invalidateQueries(['admin', 'actors']); qc.invalidateQueries(['actors']); },
  });
}

export function useUpdateActor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name, bio, imageFile }) => adminApi.updateActor(id, name, bio, imageFile),
    onSuccess: () => { qc.invalidateQueries(['admin', 'actors']); qc.invalidateQueries(['actors']); },
  });
}

export function useDeleteActor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminApi.deleteActor(id),
    onSuccess: () => { qc.invalidateQueries(['admin', 'actors']); qc.invalidateQueries(['actors']); },
  });
}

export function useAddCastMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, actorId, characterName, sortOrder }) =>
      adminApi.addCastMember(movieId, actorId, characterName, sortOrder),
    onSuccess: (_, { movieId }) => qc.invalidateQueries(['movieCast', movieId]),
  });
}

export function useRemoveCastMember() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, actorId }) => adminApi.removeCastMember(movieId, actorId),
    onSuccess: (_, { movieId }) => qc.invalidateQueries(['movieCast', movieId]),
  });
}

// ── Admin: Movie classification (Content Type / Coming Soon / Kids Content) ─────

export function useSetMovieContentType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, contentType }) => adminApi.setMovieContentType(movieId, contentType),
    onSuccess: () => { qc.invalidateQueries(['movies']); qc.invalidateQueries(['home']); },
  });
}

export function useSetMovieComingSoon() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, isComingSoon, releaseDate }) => adminApi.setMovieComingSoon(movieId, isComingSoon, releaseDate),
    onSuccess: () => { qc.invalidateQueries(['movies']); qc.invalidateQueries(['home']); },
  });
}

// Parental controls: tag/untag a title as safe for a Kids profile (Movie.IsKidsContent).
export function useSetMovieKidsContent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, isKidsContent }) => adminApi.setMovieKidsContent(movieId, isKidsContent),
    onSuccess: () => {
      qc.invalidateQueries(['admin', 'catalog']);
      qc.invalidateQueries(['movies']);
      qc.invalidateQueries(['home']);
    },
  });
}

// ── Admin: Edit already-uploaded movies / episodes (incl. reupload) ──────────

export function useAdminMovieDetail(movieId, enabled = true) {
  return useQuery({
    queryKey: ['admin', 'movie', movieId],
    queryFn: async () => {
      const { data } = await adminApi.getMovieForEdit(movieId);
      if (!data.success) throw new Error(data.message || 'Failed to load movie');
      return data.data;
    },
    enabled: enabled && !!movieId,
  });
}

export function useUpdateMovie() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ movieId, payload }) => adminApi.updateMovie(movieId, payload),
    onSuccess: (_, { movieId }) => {
      qc.invalidateQueries(['admin', 'catalog']);
      qc.invalidateQueries(['admin', 'movie', movieId]);
      qc.invalidateQueries(['movies']);
      qc.invalidateQueries(['movie', String(movieId)]);
      qc.invalidateQueries(['home']);
    },
  });
}

export function useUpdateEpisode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ episodeId, payload }) => adminApi.updateEpisode(episodeId, payload),
    onSuccess: (_, { movieId }) => {
      if (movieId) qc.invalidateQueries(['admin', 'movie', movieId]);
      qc.invalidateQueries(['admin', 'catalog']);
      qc.invalidateQueries(['movies']);
    },
  });
}

// ── Admin: Categories ────────────────────────────────────────

export function useAdminCatalog(page = 1, pageSize = 30, search = '') {
  return useQuery({
    queryKey: ['admin', 'catalog', page, pageSize, search],
    queryFn: async () => {
      const { data } = await adminApi.getCatalog(page, pageSize, search);
      if (!data.success) throw new Error('Failed to load catalog');
      return data;
    },
  });
}

export function useAdminCategories() {
  return useQuery({
    queryKey: ['admin', 'categories'],
    queryFn: async () => {
      const { data } = await adminApi.getCategories();
      if (!data.success) throw new Error('Failed to load categories');
      return data.data;
    },
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name) => adminApi.createCategory(name),
    onSuccess: () => { qc.invalidateQueries(['admin', 'categories']); qc.invalidateQueries(['categories']); },
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }) => adminApi.updateCategory(id, name),
    onSuccess: () => { qc.invalidateQueries(['admin', 'categories']); qc.invalidateQueries(['categories']); },
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => adminApi.deleteCategory(id),
    onSuccess: () => { qc.invalidateQueries(['admin', 'categories']); qc.invalidateQueries(['categories']); },
  });
}

export function useContentCreators() {
  return useQuery({
    queryKey: ['admin', 'content-creators'],
    queryFn: async () => {
      const { data } = await adminApi.getContentCreators();
      if (!data.success) throw new Error(data.message || 'Failed to load content creators');
      return data.data || [];
    },
  });
}

// ── Admin: Content Creator upload access ────────────────────────────────────────

export function useGrantUploadAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId) => adminApi.grantUploadAccess(userId),
    onSuccess: () => qc.invalidateQueries(['admin', 'content-creators']),
  });
}

export function useRevokeUploadAccess() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId) => adminApi.revokeUploadAccess(userId),
    onSuccess: () => qc.invalidateQueries(['admin', 'content-creators']),
  });
}

export function useUploadCategoryImage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) => adminApi.uploadCategoryImage(id, file),
    onSuccess: () => { qc.invalidateQueries(['admin', 'categories']); qc.invalidateQueries(['categories']); },
  });
}
